import { userAccountLoginPath } from '@digitale-verhuurders-verklaring/core/src/configuration/pathList';
import {
  ROLE_TYPES,
  roleTypes,
} from '@digitale-verhuurders-verklaring/core/src/models/functions/authentication/types';
import { createRouter, createWebHistory } from 'vue-router';

import { authGuard } from './authGuard';
import routes from './routes';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/woningnet',
      name: routes.woningNet,
      component: () => import('../pages/WoningNetPage.vue'),
      meta: {
        // WoningNet route handles its own authentication
        requiredAuth: false,
        isWoningNetRoute: true,
      },
    },
    {
      path: '/',
      name: routes.home,
      meta: {
        requiredAuth: true,
        roles: roleTypes.array().parse([ROLE_TYPES.ISSUER, ROLE_TYPES.TENANT]),
      },
      component: () => import('../pages/IntroductionPage.vue'),
    },
    {
      path: '/dashboard',
      name: routes.dashboard,
      meta: {
        requiredAuth: true,
        roles: roleTypes
          .array()
          .parse([
            ROLE_TYPES.REQUESTER,
            ROLE_TYPES.LOCATION_ADMIN,
            ROLE_TYPES.READER,
            ROLE_TYPES.ASSESSOR,
            ROLE_TYPES.ISSUER,
          ]),
      },
      component: () => import('../pages/DashboardPage.vue'),
    },
    {
      path: '/tenant-dashboard',
      name: routes.tenantDashboard,
      component: () => import('../pages/TenantDashboardPage.vue'),
      meta: {
        requiredAuth: true,
        roles: roleTypes.array().parse([ROLE_TYPES.TENANT]),
      },
    },
    {
      path: '/magic-link/:token',
      name: routes.handleMagicLink,
      component: () => import('../pages/MagicLinkHandlerPage.vue'),
    },
    {
      path: userAccountLoginPath,
      name: routes.newLandlordLogin,
      component: () => import('../pages/LogInPage.vue'),
      props: {
        isNewLandlord: true,
      },
    },
    {
      path: '/auth',
      children: [
        {
          path: '',
          name: routes.auth,
          component: () => import('../pages/LogInPage.vue'),
        },
        {
          path: '/link-expired/:email',
          name: routes.linkExpired,
          component: () => import('../pages/LinkExpiredPage.vue'),
        },
        {
          path: 'login',
          name: routes.login,
          component: () => import('../pages/LogInPage.vue'),
        },
      ],
    },
    {
      path: '/settings',
      name: routes.settings,
      component: () => import('../pages/settings/SettingsPage.vue'),
      meta: {
        requiredAuth: true,
        roles: roleTypes
          .array()
          .parse([
            ROLE_TYPES.ADMIN,
            ROLE_TYPES.LOCATION_ADMIN,
            ROLE_TYPES.ASSESSOR,
            ROLE_TYPES.READER,
            ROLE_TYPES.REQUESTER,
            ROLE_TYPES.ISSUER,
          ]),
      },
      children: [
        {
          path: '/settings/organisation',
          redirect: { name: routes.settingsOrganisationOverview },
          name: routes.settingsOrganisation,
          component: () => import('../pages/settings/OrganisationPage.vue'),
          children: [
            {
              path: '',
              name: routes.settingsOrganisationOverview,
              meta: {
                requiredAuth: true,
                roles: roleTypes.array().parse([ROLE_TYPES.ADMIN]),
              },
              component: () =>
                import('../pages/settings/OrganisationOverviewPage.vue'),
            },
            {
              path: ':organisationId',
              name: routes.settingsOrganisationDetail,
              component: () =>
                import('../pages/settings/OrganisationDetailPage.vue'),
              meta: {
                requiredAuth: true,
                roles: roleTypes
                  .array()
                  .parse([ROLE_TYPES.ADMIN, ROLE_TYPES.LOCATION_ADMIN]),
              },
            },
          ],
        },
        {
          path: '/settings/notification',
          name: routes.settingsNotification,
          component: () => import('../pages/settings/NotificationPage.vue'),
          meta: {
            requiredAuth: true,
            roles: roleTypes
              .array()
              .parse([
                ROLE_TYPES.LOCATION_ADMIN,
                ROLE_TYPES.ASSESSOR,
                ROLE_TYPES.READER,
                ROLE_TYPES.REQUESTER,
                ROLE_TYPES.ISSUER,
              ]),
          },
        },
      ],
    },
    {
      path: '/new-landlord',
      name: routes.newLandlord,
      meta: {
        requiredAuth: true,
        roles: roleTypes
          .array()
          .parse([ROLE_TYPES.REQUESTER, ROLE_TYPES.LOCATION_ADMIN]),
      },
      component: () => import('../pages/NewLandlordPage.vue'),
    },
    {
      path: '/new-landlord-assessment/:caseId',
      name: routes.newLandlordAssessment,
      meta: {
        requiredAuth: true,
        roles: roleTypes
          .array()
          .parse([ROLE_TYPES.ASSESSOR, ROLE_TYPES.LOCATION_ADMIN]),
      },
      component: () => import('../pages/NewLandlordAssessmentPage.vue'),
    },
    {
      path: '/request-current-landlord/:caseId',
      name: routes.requestCurrentLandlord,
      meta: {
        requiredAuth: true,
        roles: roleTypes.array().parse([ROLE_TYPES.TENANT]),
      },
      component: () => import('../pages/RequestCurrentLandlordPage.vue'),
    },
    {
      path: '/current-landlord-assessment/:caseId',
      name: routes.currentLandlordAssessment,
      component: () => import('../pages/CurrentLandlordAssessmentPage.vue'),
      props: true,
      meta: {
        requiredAuth: true,
        roles: roleTypes
          .array()
          .parse([ROLE_TYPES.ISSUER, ROLE_TYPES.LOCATION_ADMIN]),
      },
    },
    {
      path: '/tenant-check/:caseId',
      name: routes.tenantCheck,
      component: () => import('../pages/TenantCheckPage.vue'),
      meta: {
        requiredAuth: true,
        roles: roleTypes.array().parse([ROLE_TYPES.TENANT]),
      },
    },
    {
      path: '/re-issue-request/:caseId/:declarationId',
      name: routes.reIssueRequest,
      component: () => import('../pages/ReIssueRequestPage.vue'),
      meta: {
        requiredAuth: true,
        roles: roleTypes.array().parse([ROLE_TYPES.TENANT]),
      },
    },
    {
      path: '/view-declaration/:declarationId',
      name: routes.viewDeclaration,
      component: () => import('../pages/ViewDeclarationPage.vue'),
      meta: {
        requiredAuth: true,
        // Deep link WRB sends on completion, opened by new landlords via SSO
        authProvider: 'woningnet',
        roles: roleTypes
          .array()
          .parse([
            ROLE_TYPES.REQUESTER,
            ROLE_TYPES.LOCATION_ADMIN,
            ROLE_TYPES.ASSESSOR,
            ROLE_TYPES.READER,
            ROLE_TYPES.ISSUER,
            ROLE_TYPES.TENANT,
          ]),
      },
    },
    {
      path: '/not-found',
      name: routes.notFound,
      component: () => import('../pages/NotFoundPage.vue'),
      meta: {
        requiredAuth: true,
      },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: routes.login },
    },
  ],
});

router.beforeEach((to) => authGuard(to));

export default router;
